import * as React from 'react'

import {
  Globe as GlobeIcon,
  Layers as LayersIcon,
  LocateFixed,
  Map as MapIcon,
  Maximize,
  Minimize,
  RotateCcw,
} from 'lucide-react'
import mapboxgl from 'mapbox-gl'
import { observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { useFullscreenElement } from '~/client/src/shared/utils/fullscreenElement'

import {
  ATTRIBUTION_TOP_PX,
  CHROME_INSET_PX,
} from '../MapBoxViewer/mapAttributionRule'
import MapSettingsModal from '../MapLayersSelector/components/MapSettingsModal'
import { IMapControlsStore } from './IMapControlsStore'
import { Projection } from './MapControlsStore'
import CompassButton from './primitives/CompassButton'
import MapControlButton from './primitives/MapControlButton'
import ZoomPill from './primitives/ZoomPill'

import './UserLocationMarker.css'

const MAP_CONTROLS_ROOT_CLASS = 'map-controls-root'
// The map's own scale, sealed by `.map-controls-root`: chrome at 5 — the MapViewPicker pill shares
// it — and what a map opens over it (composer card, Dock, markup toolbars) at 10 and up.
const CHROME_Z_INDEX = 5

// One inset for everything a map puts in its corners, so lifting the chrome moves all of it.
const CHROME_INSET = `var(--map-chrome-inset, ${CHROME_INSET_PX}px)`
const CHROME_ATTRIBUTION_TOP = `var(--map-attribution-top, ${ATTRIBUTION_TOP_PX}px)`
const CHROME_INSET_PROPERTY = '--map-chrome-inset'
const ATTRIBUTION_TOP_PROPERTY = '--map-attribution-top'
const CHROME_ANCHOR_ATTRIBUTE = 'mapChromeAnchor'

// Removes only the class we added: a consumer may carry map-controls-root in its own className.
function clearChrome(container: HTMLElement, didAddRootClass: boolean): void {
  if (didAddRootClass) {
    container.classList.remove(MAP_CONTROLS_ROOT_CLASS)
  }
  delete container.dataset[CHROME_ANCHOR_ATTRIBUTE]
  container.style.removeProperty(CHROME_INSET_PROPERTY)
  container.style.removeProperty(ATTRIBUTION_TOP_PROPERTY)
}
// Compared before writing: bare consumers re-render per camera frame, and an unconditional
// setProperty invalidates style on every one of them.
const setChromeProperty = (
  container: HTMLElement,
  property: string,
  value: string,
): void => {
  if (container.style.getPropertyValue(property) !== value) {
    container.style.setProperty(property, value)
  }
}

type Anchor = 'top' | 'bottom'
type Side = 'left' | 'right'

interface IMapControlsContext {
  store: IMapControlsStore
  anchor: Anchor
  containerRef: React.RefObject<HTMLElement>
  isMobile: boolean
}

const MapControlsContext = React.createContext<IMapControlsContext | null>(null)

function useMapControls(): IMapControlsContext {
  const ctx = React.useContext(MapControlsContext)
  if (!ctx) {
    throw new Error('MapControls.* must be used inside MapControls.Provider')
  }
  return ctx
}

interface IProviderProps {
  store: IMapControlsStore
  anchor?: Anchor
  bare?: boolean
  containerRef?: React.RefObject<HTMLElement>
  isMobile?: boolean
  children: React.ReactNode
}

function Provider({
  store,
  anchor = 'bottom',
  bare,
  containerRef: externalRef,
  isMobile = false,
  children,
}: IProviderProps) {
  const internalRef = React.useRef<HTMLDivElement>(null)
  // The element we tagged, not the ref's current target: a bare consumer's ref can repoint.
  const taggedContainer = React.useRef<HTMLElement | null>(null)
  const hasAddedRootClass = React.useRef(false)
  const containerRef = externalRef ?? internalRef

  const ctx = React.useMemo(
    () => ({ store, anchor, containerRef, isMobile }),
    [store, anchor, containerRef, isMobile],
  )

  const insetLength = `${CHROME_INSET_PX}px`
  // Where the attribution row ends, for an overlay that rests on it (the mobile card carousel).
  const attributionTop = ATTRIBUTION_TOP_PX

  // Bare consumers own the element holding both controls and map, so it is theirs that gets tagged.
  // No dependency list: such a ref can populate on a later render.
  React.useEffect(() => {
    const container = bare ? containerRef.current : null
    if (!container) {
      return
    }
    if (taggedContainer.current && taggedContainer.current !== container) {
      clearChrome(taggedContainer.current, hasAddedRootClass.current)
      hasAddedRootClass.current = false
    }
    taggedContainer.current = container
    if (!container.classList.contains(MAP_CONTROLS_ROOT_CLASS)) {
      container.classList.add(MAP_CONTROLS_ROOT_CLASS)
      hasAddedRootClass.current = true
    }
    if (container.dataset[CHROME_ANCHOR_ATTRIBUTE] !== anchor) {
      container.dataset[CHROME_ANCHOR_ATTRIBUTE] = anchor
    }
    setChromeProperty(container, CHROME_INSET_PROPERTY, insetLength)
    setChromeProperty(
      container,
      ATTRIBUTION_TOP_PROPERTY,
      `${attributionTop}px`,
    )
  })

  // Unmount only: tearing down per render would strip a class React has no reason to rewrite.
  React.useEffect(
    () => () => {
      if (taggedContainer.current) {
        clearChrome(taggedContainer.current, hasAddedRootClass.current)
      }
    },
    [],
  )

  if (bare) {
    return (
      <MapControlsContext.Provider value={ctx}>
        {children}
      </MapControlsContext.Provider>
    )
  }

  return (
    <MapControlsContext.Provider value={ctx}>
      <div
        ref={internalRef}
        data-map-chrome-anchor={anchor}
        style={
          {
            [CHROME_INSET_PROPERTY]: insetLength,
            [ATTRIBUTION_TOP_PROPERTY]: `${attributionTop}px`,
          } as React.CSSProperties
        }
        className={`${MAP_CONTROLS_ROOT_CLASS} tw:relative tw:w-full tw:h-full tw:min-h-0 tw:min-w-0 tw:overflow-hidden`}
      >
        {children}
      </div>
    </MapControlsContext.Provider>
  )
}

// Only the controls take the pointer: the gaps between them are still map.
const COLUMN_CLASS =
  'tw:absolute tw:flex tw:flex-col tw:gap-1 tw:pointer-events-none tw:[&>*]:pointer-events-auto'

// Alignment is stated, not inherited: flex would stretch a control whose width is not fixed. Picked
// from a constant rather than composed per render — a bare consumer re-renders per camera frame.
const COLUMN_SIDE_CLASS: Record<Side, string> = {
  right: `${COLUMN_CLASS} tw:items-end`,
  left: `${COLUMN_CLASS} tw:items-start`,
}

interface IColumnProps {
  side: Side
  // Replaces the inset on the edge the provider anchors to, for a surface that stacks something else
  // in that corner — the view-picker pill BaseMapView's mobile controls hang below.
  anchorInset?: string
  children: React.ReactNode
}

// One positioned box per corner: the controls inside are plain flex items, so the pitch is the gap
// and no control has to know how tall its neighbours are.
function Column({ side, anchorInset, children }: IColumnProps) {
  const { anchor } = useMapControls()

  // Both axes off the shared inset, not a captured number, so the column and the credits move
  // together.
  const style: React.CSSProperties = {
    [side]: CHROME_INSET,
    [anchor]: anchorInset ?? CHROME_INSET,
    zIndex: CHROME_Z_INDEX,
  }

  return (
    <div style={style} className={COLUMN_SIDE_CLASS[side]}>
      {children}
    </div>
  )
}

function useBearing(store: IMapControlsStore): number {
  const [bearing, setBearing] = React.useState(0)

  React.useEffect(() => {
    let map: mapboxgl.Map | null = null
    let raf: number | null = null

    const onRotate = () => {
      if (raf != null) return
      raf = requestAnimationFrame(() => {
        raf = null
        if (map) setBearing(map.getBearing())
      })
    }

    const dispose = store.onMapReady(m => {
      if (map) map.off('rotate', onRotate)
      map = m
      m.on('rotate', onRotate)
      setBearing(m.getBearing())
    })

    return () => {
      dispose()
      if (raf != null) cancelAnimationFrame(raf)
      map?.off('rotate', onRotate)
    }
  }, [store])

  return bearing
}

function Compass() {
  const { store } = useMapControls()
  const bearing = useBearing(store)

  const handleClick = React.useCallback(() => {
    store.getMap()?.resetNorth()
  }, [store])

  return <CompassButton bearing={bearing} onResetNorth={handleClick} />
}

function Zoom() {
  const { store } = useMapControls()

  const handleZoomIn = React.useCallback(() => {
    store.getMap()?.zoomIn()
  }, [store])

  const handleZoomOut = React.useCallback(() => {
    store.getMap()?.zoomOut()
  }, [store])

  return <ZoomPill onZoomIn={handleZoomIn} onZoomOut={handleZoomOut} />
}

type GeolocateState = 'idle' | 'waiting' | 'active' | 'error'

function Geolocate() {
  const { store } = useMapControls()
  const controlRef = React.useRef<mapboxgl.GeolocateControl | null>(null)
  const [state, setState] = React.useState<GeolocateState>('idle')

  React.useEffect(() => {
    let control: mapboxgl.GeolocateControl | null = null
    let registeredMap: mapboxgl.Map | null = null

    const onGeolocate = () => setState('active')
    const onError = () => setState('error')
    const onTrackEnd = () => setState('idle')

    const dispose = store.onMapReady(map => {
      if (registeredMap === map) return
      if (registeredMap && control && registeredMap.hasControl(control)) {
        control.off('geolocate', onGeolocate)
        control.off('error', onError)
        control.off('trackuserlocationend', onTrackEnd)
        registeredMap.removeControl(control)
      }
      control = new mapboxgl.GeolocateControl({
        positionOptions: { enableHighAccuracy: true },
        trackUserLocation: true,
        showUserLocation: true,
        showUserHeading: true,
      })
      control.on('geolocate', onGeolocate)
      control.on('error', onError)
      control.on('trackuserlocationend', onTrackEnd)
      map.addControl(control, 'bottom-right')
      registeredMap = map
      controlRef.current = control
    })

    return () => {
      dispose()
      if (registeredMap && control && registeredMap.hasControl(control)) {
        control.off('geolocate', onGeolocate)
        control.off('error', onError)
        control.off('trackuserlocationend', onTrackEnd)
        registeredMap.removeControl(control)
      }
      controlRef.current = null
    }
  }, [store])

  const handleClick = React.useCallback(() => {
    if (!controlRef.current) return
    setState('waiting')
    controlRef.current.trigger()
  }, [])

  return (
    <MapControlButton onClick={handleClick} title="My Location">
      <LocateFixed
        className={classList({
          'tw:text-primary': state === 'active',
          'tw:text-destructive': state === 'error',
          'tw:animate-spin': state === 'waiting',
        })}
      />
    </MapControlButton>
  )
}

function Fullscreen() {
  const { containerRef } = useMapControls()
  // Shared tracker — this component isn't an observer, so go through the hook adapter.
  const isFullscreen = useFullscreenElement() === containerRef.current

  const handleClick = React.useCallback(() => {
    const el = containerRef.current
    if (!el || !document.fullscreenEnabled) return

    if (document.fullscreenElement === el) {
      document.exitFullscreen()
    } else {
      el.requestFullscreen()
    }
  }, [containerRef])

  return (
    <MapControlButton onClick={handleClick} title="Fullscreen">
      {isFullscreen ? <Minimize /> : <Maximize />}
    </MapControlButton>
  )
}

const Reset = observer(function Reset() {
  const { store } = useMapControls()

  const handleClick = React.useCallback(() => {
    store.resetViewport()
  }, [store])

  return (
    <MapControlButton onClick={handleClick} title="Reset Viewport">
      <RotateCcw />
    </MapControlButton>
  )
})

const ProjectionToggle = observer(function ProjectionToggle() {
  const { store } = useMapControls()

  return (
    <MapControlButton onClick={store.toggleProjection} title="Projection">
      {store.projection === Projection.globe ? <GlobeIcon /> : <MapIcon />}
    </MapControlButton>
  )
})

const Layers = observer(function Layers() {
  const { store, isMobile } = useMapControls()

  return (
    <MapSettingsModal
      trigger={
        <MapControlButton title="Layers">
          <LayersIcon />
        </MapControlButton>
      }
      settings={store.mapSettings}
      isMobile={isMobile}
    />
  )
})

interface IChromeProps {
  anchorInset?: string
  // False where the surface keeps controls of its own outside the map — a fullscreen map strands
  // them.
  hasFullscreen?: boolean
  // False where globe/mercator switching isn't offered (e.g. the User Portal projects map).
  hasProjectionToggle?: boolean
  // A surface's own controls, at the top of the Layers column.
  leftExtra?: React.ReactNode
}

// What a map surface puts in its corners. Composed here rather than at each surface so the order and
// the mobile rules are stated once; a surface that needs something else builds its own Columns.
function Chrome({
  anchorInset,
  hasFullscreen = true,
  hasProjectionToggle = true,
  leftExtra,
}: IChromeProps) {
  const { isMobile } = useMapControls()

  return (
    <>
      <Column side="right" anchorInset={anchorInset}>
        <Compass />
        <Geolocate />
        {/* Mobile pinches to zoom, and has no window to fill. */}
        {!isMobile && <Zoom />}
        <Reset />
        {hasProjectionToggle && <ProjectionToggle />}
        {!isMobile && hasFullscreen && <Fullscreen />}
      </Column>
      <Column side="left" anchorInset={anchorInset}>
        {leftExtra}
        <Layers />
      </Column>
    </>
  )
}

// Full-width slot resting on the credits row, for anything a map floats along its bottom edge —
// a card strip, a hint, a menu. Named so the clearance is stated once instead of at each surface.
const AboveAttribution: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div
    className="tw:absolute tw:left-0 tw:right-0 tw:z-[8]"
    style={{ bottom: CHROME_ATTRIBUTION_TOP }}
  >
    {children}
  </div>
)

export const MapControls = {
  AboveAttribution,
  Provider,
  Chrome,
  Column,
  Compass,
  Zoom,
  Geolocate,
  Fullscreen,
  Reset,
  ProjectionToggle,
  Layers,
}
